import { VersionedViewModelBase } from '@/components/basedata/VersionedViewModelBase'
import ImageVault from '@/data/ImageVaultData'
import { centerBrandsMapper } from '@/store/modules/centerBrands'
import { servicesMapper } from '@/store/modules/services'
import {
  Center,
  CenterContact,
  CenterLgVersion,
  CenterOpenPeriod,
  CenterService,
  CenterSpecialOpenPeriod} from '@/types/centers'
import { ServiceLgVersion } from '@/types/services'
import { localeSort } from '@/utils/formatting'
import { getLgVersion } from '@/utils/language'
import { formatLocationAsNo, formatLocationAsUs, isUsFormatLocation } from '@/utils/location'

export interface LookupItem {
  id: number
  name: string
}

export default VersionedViewModelBase<Center, CenterLgVersion>().extend({
  name: 'CenterViewModel',

  data: () => {
    return {
      // Select items:
      countries: [
        {
          countryCode: 'NO',
          country: 'Norge'
        },
        {
          countryCode: 'SE',
          country: 'Sverige'
        }
      ]
    }
  },

  computed: {
    ...centerBrandsMapper.mapState({
      allCenterBrands: 'centerBrands'
    }),
    ...servicesMapper.mapState({
      allServices: 'services'
    }),

    isNew (): boolean {
      return !this.model.centerId
    },

    // Model:

    centerId: {
      get (): number {
        return this.model.centerId
      },
      set (value: number) {
        this.model.centerId = value
      }
    },
    centerBrandId: {
      get (): number {
        return this.model.centerBrandId
      },
      set (value: number) {
        this.model.centerBrandId = value
      }
    },
    centerBrandName (): string {
      const centerBrand = this.allCenterBrands.find(x => x.centerBrandId == this.model.centerBrandId)
      return centerBrand ? centerBrand.name : ''
    },
    address: {
      get (): string | null {
        return this.model.address
      },
      set (value: string | null) {
        this.model.address = value
      }
    },
    postalCode: {
      get (): string | null {
        return this.model.postalCode
      },
      set (value: string | null) {
        this.model.postalCode = value
      }
    },
    city: {
      get (): string | null {
        return this.model.city
      },
      set (value: string | null) {
        this.model.city = value
      }
    },
    countryCode: {
      get (): string | null {
        return this.model.countryCode
      },
      set (value: string | null) {
        this.model.countryCode = value
        this.model.country = value
          ? this.countries[this.countries.findIndex(x => x.countryCode == value)]!.country
          : null
      }
    },
    country: {
      get (): string | null {
        return this.model.country
      },
      set (value: string | null) {
        this.model.country = value
        this.model.countryCode = value
          ? this.countries[this.countries.findIndex(x => x.country == value)]!.countryCode
          : null
      }
    },
    locationMapsLink (): string | null {
      if (!this.model.location) return null
      return 'http://www.google.com/maps/place/' + formatLocationAsUs(this.model.location)
    },
    location: {
      get (): string | null {
        return this.model.location
      },
      set (value: string | null) {
        this.model.location = value
      }
    },
    viametricsId: {
      get (): number | null {
        return this.model.viametricsId
      },
      set (value: number | null) {
        this.model.viametricsId = value
        if (!value) {
          this.model.viametricsId = this.model.viametricsCounterGroupId = null
        }
      }
    },
    viametricsCounterGroupId: {
      get (): number | null {
        return this.model.viametricsCounterGroupId
      },
      set (value: number | null) {
        this.model.viametricsCounterGroupId = value
      }
    },
    imasId: {
      get (): string | null {
        return this.model.imasId
      },
      set (value: string | null) {
        this.model.imasId = value
      }
    },
    visitorCapacity: {
      get (): number | null {
        return this.model.visitorCapacity
      },
      set (value: number | null) {
        this.model.visitorCapacity = value
      }
    },
    visitorThreshold: {
      get (): number | null {
        return this.model.visitorThreshold
      },
      set (value: number | null) {
        this.model.visitorThreshold = value
      }
    },
    businessLevelMedium: {
      get (): number | null {
        return this.model.businessLevelMedium
      },
      set (value: number | null) {
        this.model.businessLevelMedium = value
      }
    },
    businessLevelHigh: {
      get (): number | null {
        return this.model.businessLevelHigh
      },
      set (value: number | null) {
        this.model.businessLevelHigh = value
      }
    },
    hasWayfinding: {
      get (): boolean {
        return this.model.hasWayfinding
      },
      set (value: boolean) {
        this.model.hasWayfinding = value ? value : false
      }
    },
    hasApp: {
      get (): boolean {
        return this.model.hasApp
      },
      set (value: boolean) {
        this.model.hasApp = value ? value : false
      }
    },
    hasGiftCard: {
      get (): boolean {
        return this.model.hasGiftCard
      },
      set (value: boolean) {
        this.model.hasGiftCard = value ? value : false
      }
    },
    temporarilyClosed: {
      get (): boolean {
        return this.model.temporarilyClosed
      },
      set (value: boolean) {
        this.model.temporarilyClosed = value ? value : false
      }
    },
    emergency: {
      get (): boolean {
        return this.model.emergency
      },
      set (value: boolean) {
        this.model.emergency = value ? value : false
      }
    },
    googlePlaceId: {
      get (): string | null {
        return this.model.googlePlaceId
      },
      set (value: string | null) {
        this.model.googlePlaceId = value
      }
    },
    loyaltySlug: {
      get (): string | null {
        return this.model.loyaltySlug
      },
      set (value: string | null) {
        this.model.loyaltySlug = value
      }
    },
    loyaltyClubId: {
      get (): number | null {
        return this.model.loyaltyClubId
      },
      set (value: number | null) {
        this.model.loyaltyClubId = value
      }
    },
    parkingIntegration: {
      get (): string | null {
        return this.model.parkingIntegration
      },
      set (value: string | null) {
        this.model.parkingIntegration = value
        if (!value) {
          this.model.parkingIntegration = this.model.parking_customerClub = null
        }
      }
    },
    parking_customerClub: {
      get (): string | null {
        return this.model.parking_customerClub
      },
      set (value: string | null) {
        this.model.parking_customerClub = value
      }
    },
    fenistraDb: {
      get (): string | null {
        return this.model.fenistraDb
      },
      set (value: string | null) {
        this.model.fenistraDb = value
      }
    },
    fenistraId: {
      get (): number | null {
        return this.model.fenistraId
      },
      set (value: number | null) {
        this.model.fenistraId = value
      }
    },
    createdDate (): Date | undefined {
      return this.model.created
    },
    lastUpdatedDate (): Date | undefined {
      return this.model.created
    },

    // lgModel:

    name: {
      get (): string {
        return this.lgModel.name
      },
      set (value: string) {
        this.lgModel.name = value
      }
    },
    logoImageVaultId: {
      get (): number | null {
        return this.lgModel.logoImageVaultId
      },
      set (value: number | null) {
        this.lgModel.logoImageVaultId = value

        ImageVault.updateUrlFromImageVaultIdDebounced(
          this.lgModel.logoImageVaultId,
          url => (this.lgModel.logoUrl = url),
          error =>
            this.$snackbar.show(
              'Kunne ikke hente url for ImageVault-ID: ' + this.lgModel.logoImageVaultId,
              'error'
            ),
          id => this.$snackbar.show('Ukjent ImageVault-ID: ' + id, 'warning')
        )
      }
    },
    logoUrl: {
      get (): string | null {
        return this.lgModel.logoUrl
      },
      set (value: string | null) {
        this.lgModel.logoUrl = value
      }
    },
    appLogoImageVaultId: {
      get (): number | null {
        return this.lgModel.appLogoImageVaultId
      },
      set (value: number | null) {
        this.lgModel.appLogoImageVaultId = value

        ImageVault.updateUrlFromImageVaultIdDebounced(
          this.lgModel.appLogoImageVaultId,
          url => (this.lgModel.appLogoUrl = url),
          error =>
            this.$snackbar.show(
              'Kunne ikke hente url for ImageVault-ID: ' + this.lgModel.appLogoImageVaultId,
              'error'
            ),
          id => this.$snackbar.show('Ukjent ImageVault-ID: ' + id, 'warning')
        )
      }
    },
    appLogoUrl: {
      get (): string | null {
        return this.lgModel.appLogoUrl
      },
      set (value: string | null) {
        this.lgModel.appLogoUrl = value
      }
    },
    homePageUrl: {
      get (): string | null {
        return this.lgModel.homePageUrl
      },
      set (value: string | null) {
        this.lgModel.homePageUrl = value
      }
    },
    contactEmail: {
      get (): string | null {
        return this.lgModel.contactEmail
      },
      set (value: string | null) {
        this.lgModel.contactEmail = value
      }
    },
    temporarilyClosedMessage: {
      get (): string | null {
        return this.lgModel.temporarilyClosedMessage
      },
      set (value: string | null) {
        this.lgModel.temporarilyClosedMessage = value
      }
    },
    emergencyBannerHeader: {
      get (): string | null {
        return this.lgModel.emergencyBannerHeader
      },
      set (value: string | null) {
        this.lgModel.emergencyBannerHeader = value
      }
    },
    emergencyBannerText: {
      get (): string | null {
        return this.lgModel.emergencyBannerText
      },
      set (value: string | null) {
        this.lgModel.emergencyBannerText = value
      }
    },
    emergencyStoreListText: {
      get (): string | null {
        return this.lgModel.emergencyStoreListText
      },
      set (value: string | null) {
        this.lgModel.emergencyStoreListText = value
      }
    },

    // Collections:

    contacts (): CenterContact[] {
      return this.model.contacts
    },
    openPeriods (): CenterOpenPeriod[] {
      return this.model.openPeriods
    },
    specialOpenPeriods (): CenterSpecialOpenPeriod[] {
      return this.model.specialOpenPeriods
    },
    emergencyOpenStores (): number[] {
      return this.model.emergencyOpenStores
    },
    services (): CenterService[] {
      return this.model.services
    },

    // Lookups:

    serviceLookups (): LookupItem[] {
      return this.allServices
        .map(x => ({
          id: x.serviceId,
          name: getLgVersion<ServiceLgVersion>(x, this.$uiLanguage).name
        }))
        .sort((a, b) => localeSort(a.name, b.name))
    }
  },

  methods: {
    ...centerBrandsMapper.mapActions({
      loadCenterBrands: 'loadCenterBrands'
    }),
    ...servicesMapper.mapActions({
      loadServices: 'loadServices'
    }),

    pasteLocation (event: any): void {
      const value = event.clipboardData.getData('text/plain')
      if (isUsFormatLocation(value)) {
        event.preventDefault()
        this.model.location = formatLocationAsNo(value)
      }
    }
  },

  mounted (): void {
    this.loadCenterBrands({ ensureOnly: true }).catch(error => {
      console.log('Kunne ikke laste kjøpesenterkjeder: ' + error)
      if (this.allCenterBrands.length > 0) console.log('Viser bufrede data.')
    })
    this.loadServices({ ensureOnly: true }).catch(error => {
      console.log('Kunne ikke laste tjenester: ' + error)
      if (this.allServices.length > 0) console.log('Viser bufrede data.')
    })
  }
})
